import type { MappingResult } from '../../lib/recognize/config';
import type { Skeleton } from '../../lib/skeleton/core';
import type {
  OutlinerHierarchy,
  OutlinerHierarchyNode,
} from '../../lib/outliner';
import {
  BONE_PROPERTIES_BADGE_GAP_PX,
  BONE_PROPERTIES_BADGE_SIZE_PX,
  hasBoneProperties,
} from '../../presentations/properties';
import {
  CHAIN_COLUMN_WIDTH,
  CHAIN_MIN_COLUMN_WIDTH,
  EXCLUDED_COLUMN_WIDTH,
  EXCLUDED_MIN_COLUMN_WIDTH,
  ROLE_COLUMN_WIDTH,
  ROLE_MIN_COLUMN_WIDTH,
  TREE_NODE_BASE_LEFT_OFFSET_PX,
  TREE_NODE_INDENT_STEP_PX,
  TREE_MIN_COLUMN_WIDTH,
} from './constants';
import type { BoneLookups, VisibleBoneRow } from './types';

const TRANSFORM_ICON_WIDTH_PX = 12;

export function buildLookups(
  mapping: MappingResult | null,
  skeleton: Skeleton,
): BoneLookups {
  const boneToRole = new Map<number, string>();
  const boneToConfidence = new Map<number, number>();
  const boneToChain = new Map<number, string>();

  if (!mapping) return { boneToRole, boneToConfidence, boneToChain };

  for (const [role, boneRef] of Object.entries(mapping.roleToBone)) {
    const boneIndex = skeleton.resolveBoneRef(boneRef, { namespace: 'internal' });
    if (boneIndex === null) continue;
    boneToRole.set(boneIndex, role);
    const conf = mapping.roleToConfidence[role];
    if (conf != null) boneToConfidence.set(boneIndex, conf);
  }

  for (const [chainName, boneRefs] of Object.entries(mapping.chains)) {
    for (const boneRef of boneRefs) {
      const boneIndex = skeleton.resolveBoneRef(boneRef, {
        namespace: 'internal',
      });
      if (boneIndex !== null) boneToChain.set(boneIndex, chainName);
    }
  }

  return { boneToRole, boneToConfidence, boneToChain };
}

export function buildDefaultExpanded(
  hierarchy: OutlinerHierarchy,
  maxDepth: number,
): Set<string> {
  const expanded = new Set<string>();
  const dfs = (nodeId: string, depth: number) => {
    const node = hierarchy.nodesById[nodeId];
    if (!node) return;
    if (node.childrenIds.length > 0 && depth < maxDepth) {
      expanded.add(nodeId);
      for (const childId of node.childrenIds) dfs(childId, depth + 1);
    }
  };
  for (const rootId of hierarchy.rootIds) {
    dfs(rootId, 0);
  }
  return expanded;
}

export function buildFullyExpanded(hierarchy: OutlinerHierarchy): Set<string> {
  const expanded = new Set<string>();

  const visit = (nodeId: string) => {
    const node = hierarchy.nodesById[nodeId];
    if (!node) return;
    if (node.childrenIds.length > 0) expanded.add(nodeId);
    for (const childId of node.childrenIds) {
      visit(childId);
    }
  };

  for (const rootId of hierarchy.rootIds) {
    visit(rootId);
  }

  return expanded;
}

function isExcludedBoneNode(
  node: OutlinerHierarchyNode,
  excludedSet: Set<number>,
): boolean {
  if (node.kind !== 'bone') return false;
  return excludedSet.has(node.boneIndex);
}

function isUnassignedBoneNode(
  node: OutlinerHierarchyNode,
  assignedBoneIndexSet: Set<number>,
): boolean {
  if (node.kind !== 'bone') return false;
  return !assignedBoneIndexSet.has(node.boneIndex);
}

function shouldHideNode(
  node: OutlinerHierarchyNode,
  excludedSet: Set<number>,
  hideExcludedBones: boolean,
  hideTransformNodes: boolean,
  hideUnassignedBones: boolean,
  assignedBoneIndexSet: Set<number> | null,
): boolean {
  if (hideTransformNodes && node.kind === 'transform') {
    return true;
  }
  if (hideExcludedBones && isExcludedBoneNode(node, excludedSet)) {
    return true;
  }
  if (
    hideUnassignedBones &&
    assignedBoneIndexSet !== null &&
    isUnassignedBoneNode(node, assignedBoneIndexSet)
  ) {
    return true;
  }
  return false;
}

export function buildHasVisibleDescendantMap(
  hierarchy: OutlinerHierarchy,
  excludedSet: Set<number>,
  hideExcludedBones: boolean,
  hideTransformNodes: boolean,
  hideUnassignedBones: boolean,
  assignedBoneIndexSet: Set<number> | null,
): Map<string, boolean> {
  const hasVisibleDescendants = new Map<string, boolean>();

  const visit = (nodeId: string): boolean => {
    const node = hierarchy.nodesById[nodeId];
    if (!node) return false;
    const isVisible = !shouldHideNode(
      node,
      excludedSet,
      hideExcludedBones,
      hideTransformNodes,
      hideUnassignedBones,
      assignedBoneIndexSet,
    );
    let childSubtreeHasVisibleRows = false;

    for (const childId of node.childrenIds) {
      if (visit(childId)) {
        childSubtreeHasVisibleRows = true;
      }
    }

    hasVisibleDescendants.set(nodeId, childSubtreeHasVisibleRows);
    return isVisible || childSubtreeHasVisibleRows;
  };

  for (const rootId of hierarchy.rootIds) {
    visit(rootId);
  }
  return hasVisibleDescendants;
}

export function buildVisibleRows(
  hierarchy: OutlinerHierarchy,
  expanded: Set<string>,
  excludedSet: Set<number>,
  hideExcludedBones: boolean,
  hideTransformNodes: boolean,
  hideUnassignedBones: boolean,
  assignedBoneIndexSet: Set<number> | null,
): VisibleBoneRow[] {
  const rows: VisibleBoneRow[] = [];

  const visit = (nodeId: string, depth: number) => {
    const node = hierarchy.nodesById[nodeId];
    if (!node) return;
    const shouldHideRow = shouldHideNode(
      node,
      excludedSet,
      hideExcludedBones,
      hideTransformNodes,
      hideUnassignedBones,
      assignedBoneIndexSet,
    );

    if (!shouldHideRow) {
      rows.push({ node, depth });
    }

    if (node.childrenIds.length === 0) return;

    // If this row is hidden by filter, continue traversing so descendants are
    // promoted visually (as if this node does not exist in the visible tree).
    const shouldTraverseChildren = shouldHideRow || expanded.has(nodeId);
    if (!shouldTraverseChildren) return;

    const childDepth = shouldHideRow ? depth : depth + 1;
    for (const childId of node.childrenIds) {
      visit(childId, childDepth);
    }
  };

  for (const rootId of hierarchy.rootIds) {
    visit(rootId, 0);
  }
  return rows;
}

export function buildRowGridTemplateColumns(
  treeColumnWidth: number,
  showExcludedColumn: boolean,
  showRolesColumn: boolean,
  showChainColumn: boolean,
): string {
  const columns = [`minmax(${treeColumnWidth}px,1fr)`];
  if (showExcludedColumn) {
    columns.push(
      `minmax(${EXCLUDED_MIN_COLUMN_WIDTH}px,${EXCLUDED_COLUMN_WIDTH}px)`,
    );
  }
  if (showRolesColumn) {
    columns.push(`minmax(${ROLE_MIN_COLUMN_WIDTH}px,${ROLE_COLUMN_WIDTH}px)`);
  }
  if (showChainColumn) {
    columns.push(`minmax(${CHAIN_MIN_COLUMN_WIDTH}px,${CHAIN_COLUMN_WIDTH}px)`);
  }
  return columns.join(' ');
}

export function measureVisibleTreeColumnWidth(
  visibleRows: VisibleBoneRow[],
  skeleton: Skeleton,
  mappingIoExtras: Record<string, unknown> = {},
): number {
  if (typeof document === 'undefined') return TREE_MIN_COLUMN_WIDTH;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return TREE_MIN_COLUMN_WIDTH;
  context.font = '500 12px Inter, sans-serif';

  let maxRequiredWidth = TREE_MIN_COLUMN_WIDTH;
  for (const { node, depth } of visibleRows) {
    const nodeLabel = node.name;
    const textWidth = context.measureText(nodeLabel).width;
    const propertyBadgeWidth =
      node.kind === 'bone' &&
      hasBoneProperties(skeleton.bone(node.boneIndex), mappingIoExtras)
        ? BONE_PROPERTIES_BADGE_SIZE_PX + BONE_PROPERTIES_BADGE_GAP_PX
        : 0;
    const transformIconWidth =
      node.kind === 'transform' ? TRANSFORM_ICON_WIDTH_PX + 4 : 0;
    const rowRequiredWidth = Math.ceil(
      depth * TREE_NODE_INDENT_STEP_PX + // hierarchy indent
        TREE_NODE_BASE_LEFT_OFFSET_PX + // base left offset
        16 + // expand/collapse slot width
        4 + // gap between slot and label
        propertyBadgeWidth +
        transformIconWidth +
        textWidth +
        10, // safety buffer
    );
    if (rowRequiredWidth > maxRequiredWidth) {
      maxRequiredWidth = rowRequiredWidth;
    }
  }
  return maxRequiredWidth;
}

export function estimateTwoCharacterWidthPx(element: HTMLElement): number {
  const styles = window.getComputedStyle(element);
  const font = `${styles.fontStyle} ${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 20;
  context.font = font;
  // Use wider glyphs for a safer minimum fit estimate.
  return context.measureText('WW').width;
}
